import { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { message as $message } from 'antd'
import { useMemoizedFn } from 'ahooks'
import AddQuickSearchModal from '../../components/AddQuickSearchModal'
import SortQuickSearchModal from '../../components/SortQuickSearchModal'
import { queryUserConfig, addUserConfig, queryConfigListForCompany, insertOrUpdateForCompany } from '@/services/user'
import { promiseAPI } from '@/utils/modal'

import getQuickSearchInitValues from '../../helper/getQuickSearchInitValues'
import getQuickSearchApplyValues from '../../helper/getQuickSearchApplyValues'
import upgradeQuickSearchConfigValues from '../../helper/upgradeQuickSearchConfigValues'
import { getIsLogin } from '@/utils/token'

const promiseAddQuickSearchModal = promiseAPI(AddQuickSearchModal)
const promiseSortQuickSearchModal = promiseAPI(SortQuickSearchModal)

const checkIsEmpty = ({ queryFields }) => {
    // triggerSearchOnChange 为自动触发搜索的字段，一般有默认值，所以这里过滤掉不计入判断
    const validFields = queryFields
        .filter(v => v.fields?.filter(v1 => v1.checkIsEmptyOnSave || !v1.triggerSearchOnChange)?.length)
        .filter(v => v.fields?.length)
    return !validFields?.length
}

const MAX_QUICK_SEARCH_NUM = 50

const checkIsMaxLimit = ({ quickSearchOptions }) => {
    return quickSearchOptions.length >= MAX_QUICK_SEARCH_NUM
}

const createConfigService = ({ configKey, configSaveOnCompamyLevel }) => {
    if (configSaveOnCompamyLevel) {
        return {
            async queryConfig(e) {
                const tConfigKey = e?.configKey || configKey

                const { success, message, data } = await queryConfigListForCompany({
                    configKey: tConfigKey
                })

                if (success) {
                    return data?.[tConfigKey]
                } else {
                    $message.error(message)

                    return Promise.reject(new Error(message))
                }
            },
            async saveConfig(configValue) {
                const { success, message } = await insertOrUpdateForCompany({
                    configKey,
                    configValue
                })

                if (success) {
                    return Promise.resolve(configValue)
                } else {
                    $message.error(message)

                    return Promise.reject(new Error(message))
                }
            },
            async addItemConfig(item) {
                const res = await queryConfigListForCompany({ configKey })

                if (!res.success) {
                    $message.error(res.message)

                    return Promise.reject(new Error(res.message))
                }

                const curList = res.data?.[configKey]?.quickSearchList || []
                const newList = [item].concat(curList.filter(v => v.key !== item.key))

                const { success, message } = await insertOrUpdateForCompany({
                    configKey,
                    configValue: {
                        quickSearchList: newList
                    }
                })

                if (!success) {
                    $message.error(message)

                    return Promise.reject(new Error(message))
                }

                $message.success('保存成功')

                return Promise.resolve(newList)
            },
            async deleteItemConfig(item) {
                const res = await queryConfigListForCompany({ configKey })

                if (!res.success) {
                    $message.error(res.message)

                    return Promise.reject(new Error(res.message))
                }

                const curList = res.data?.[configKey]?.quickSearchList || []
                const newList = curList.filter(v => v.key !== item.key)

                const { success, message } = await insertOrUpdateForCompany({
                    configKey,
                    configValue: {
                        quickSearchList: newList
                    }
                })

                if (!success) {
                    $message.error(message)

                    return Promise.reject(new Error(message))
                }

                return Promise.resolve(newList)
            }
        }
    }

    return {
        async queryConfig(e) {
            const tConfigKey = e?.configKey || configKey

            const { success, message, data } = await queryUserConfig({
                configKey: tConfigKey
            })

            if (success) {
                return data?.[tConfigKey]
            } else {
                $message.error(message)

                return Promise.reject(new Error(message))
            }
        },
        async saveConfig(configValue) {
            const { success, message } = await addUserConfig({
                configKey,
                configValue
            })

            if (success) {
                return Promise.resolve(configValue)
            } else {
                $message.error(message)

                return Promise.reject(new Error(message))
            }
        },
        async addItemConfig(item, list) {
            const newList = [item].concat(list)

            const { success, message } = await addUserConfig({
                configKey,
                configValue: {
                    quickSearchList: newList
                }
            })

            if (success) {
                $message.success('保存成功')

                return Promise.resolve(newList)
            } else {
                $message.error(message)

                return Promise.reject(new Error(message))
            }
        },
        async deleteItemConfig(item, list) {
            const newList = list.filter(v => v.key !== item.key)

            const { success, message } = await addUserConfig({
                configKey,
                configValue: {
                    quickSearchList: newList
                }
            })

            if (success) {
                return Promise.resolve(newList)
            } else {
                $message.error(message)

                return Promise.reject(new Error(message))
            }
        }
    }
}

/**
 * 检测是否需要升级配置
 * 存在老的 "filters" 字段且不存在新的 "quickSearchList" 字段
 */
const checkConfigNeedUpgrade = config => {
    return !!config?.filters?.length && !config?.quickSearchList
}

export default ({
    name,
    form,
    fieldsGroup,
    sortFields,
    sortValues,
    quickSearchEmptyMessage,
    quickSearchConfigKey,
    quickSearchOldConfigKey,
    selected,
    defaultSortText,
    configSaveOnCompamyLevel,
    onApplyQuickSearch
}: any) => {
    const [quickSearchInited, setQuickSearchInited] = useState(false)
    const [quickSearchOptions, setQuickSearchOptions] = useState<any[]>([])
    const [quickSearchLoading, setQuickSearchLoading] = useState<boolean>(false)
    const [needUpgradeQuickSearchConfig, setUpgradeQuickSearchConfig] = useState(false) // 是否需要升级配置
    const [quickSearchUpgrading, setQuickSearchUpgrading] = useState(false) // 正在升级
    const isLogin = getIsLogin()
    //  优先使用 quickSearchConfigKey和旧的configKey保持一致，相比 quickSearchOldConfigKey减少一次查询
    const configKey = quickSearchConfigKey || `${name}_quickSearch`

    const configServRef = useRef(null)

    configServRef.current = createConfigService({ configKey, configSaveOnCompamyLevel })

    const { queryConfig, saveConfig, addItemConfig, deleteItemConfig } = configServRef.current

    // 初始化快捷查询配置
    const initQuickSearchConfig = useMemoizedFn(async () => {
        setQuickSearchLoading(true)

        try {
            const [config, oldConfig] = await Promise.all([
                queryConfig({ configKey }),
                // 兼容旧配置
                quickSearchOldConfigKey ? queryConfig({ configKey: quickSearchOldConfigKey }) : Promise.resolve()
            ])

            const quickSearchList = config?.quickSearchList
            const safeOptions = Array.isArray(quickSearchList) ? quickSearchList : []

            const isNeedUpgrade = checkConfigNeedUpgrade({ ...oldConfig, ...config })

            ReactDOM.unstable_batchedUpdates(() => {
                setQuickSearchOptions(
                    safeOptions.map((v, k) => ({
                        ...v,
                        key: v.key || String(k)
                    }))
                )
                setQuickSearchInited(true)
                setQuickSearchLoading(false)
                setUpgradeQuickSearchConfig(isNeedUpgrade)

                if (isNeedUpgrade) {
                    upgradeQuickSearchConfig()
                }
            })
        } catch (e) {
            console.error(e)

            ReactDOM.unstable_batchedUpdates(() => {
                setQuickSearchLoading(false)
                setQuickSearchInited(true)
                setUpgradeQuickSearchConfig(false)
            })
        }
    })

    // 添加快捷查询配置
    const addQuickSearchConfig = useMemoizedFn(async () => {
        // 达到上限
        if (checkIsMaxLimit({ quickSearchOptions })) {
            $message.warning(`最多可添加${MAX_QUICK_SEARCH_NUM}个快捷查询条件，请您删除不需要的快捷查询条件`)
            return
        }

        const values = form.getFieldsValue()

        const preInitValues = getQuickSearchInitValues({
            form,
            fieldsGroup,
            values,
            sortFields,
            sortValues
        })

        // 无筛选条件
        if (checkIsEmpty(preInitValues)) {
            $message.warn(quickSearchEmptyMessage)
            return
        }

        const wainFunc = (e: any) => {
            return addItemConfig(e, quickSearchOptions)
        }

        const { data, waitRes } = await promiseAddQuickSearchModal({
            form,
            fieldsGroup,
            values,
            preInitValues,
            sortFields,
            defaultSortText,
            sortValues,
            wainFunc,
            resolveWaitRes(e: any) {
                return e
            }
        })

        ReactDOM.unstable_batchedUpdates(() => {
            setQuickSearchOptions(waitRes)
            onApplyQuickSearch(getQuickSearchApplyValues({ quickItem: data, fieldsGroup, sortFields }))
        })
    })

    // 快捷查询配置排序
    const sortQuickSearchConfig = useMemoizedFn(async () => {
        const wainFunc = (e: any) => {
            return saveConfig({
                quickSearchList: e.list
            })
        }

        const { data } = await promiseSortQuickSearchModal({
            quickSearchOptions,
            wainFunc,
            resolveWaitRes(e: any) {
                return e
            }
        })

        setQuickSearchOptions(data.list)

        $message.success('保存成功')
    })

    // 删除快捷查询配置
    const deleteQuickSearchConfig = async (target: any) => {
        const newList = await deleteItemConfig(target, quickSearchOptions)

        setQuickSearchOptions(newList)
    }

    // 升级快捷查询配置
    const upgradeQuickSearchConfig = useMemoizedFn(async () => {
        setQuickSearchUpgrading(true)

        try {
            const [config, oldConfig] = await Promise.all([
                queryConfig({ configKey }),
                // 兼容旧配置
                quickSearchOldConfigKey ? queryConfig({ configKey: quickSearchOldConfigKey }) : Promise.resolve()
            ])

            const taregtConfig = quickSearchOldConfigKey ? oldConfig : config
            const isNeedUpgrade = checkConfigNeedUpgrade(taregtConfig)

            if (isNeedUpgrade) {
                const newList = await upgradeQuickSearchConfigValues({
                    form,
                    fieldsGroup,
                    sortFields,
                    sortValues,
                    config: taregtConfig
                })

                await saveConfig({
                    quickSearchList: newList
                })

                $message.success('快捷查询配置升级成功')

                ReactDOM.unstable_batchedUpdates(() => {
                    setQuickSearchOptions(newList)
                    setUpgradeQuickSearchConfig(false)
                    setQuickSearchUpgrading(false)
                })
            } else {
                $message.success('快捷查询配置升级成功')

                const quickSearchList = taregtConfig?.quickSearchList

                const safeOptions = Array.isArray(quickSearchList) ? quickSearchList : []

                ReactDOM.unstable_batchedUpdates(() => {
                    setQuickSearchOptions(
                        safeOptions.map((v, k) => ({
                            ...v,
                            key: v.key || String(k)
                        }))
                    )
                    setUpgradeQuickSearchConfig(false)
                    setQuickSearchUpgrading(false)
                })
            }
        } catch (e) {
            console.error('upgradeQuickSearchConfig:', e)
            setQuickSearchUpgrading(false)
            $message.error('快捷查询配置升级失败')
        }
    })

    useEffect(() => {
        if (isLogin) {
            initQuickSearchConfig()
        }
    }, [configKey, quickSearchOldConfigKey])

    return {
        addQuickSearchConfig,
        sortQuickSearchConfig,
        deleteQuickSearchConfig,
        upgradeQuickSearchConfig,
        quickSearchOptions,
        quickSearchLoading,
        quickSearchSelected: selected,
        setQuickSearchSelected: onApplyQuickSearch,
        needUpgradeQuickSearchConfig,
        quickSearchInited,
        quickSearchUpgrading
    }
}
